/* eslint-disable */

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AutorenewIcon from 'mdi-react/AutorenewIcon';
import WalletIcon from 'mdi-react/WalletIcon';
import { Card, CardBody, Col, Row } from 'reactstrap';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import CountRequestType from './CountRequestType';
import { getToken } from '../../../services/auth';
import { brMoneyMask } from '../../../shared/utils/utils';
import { useModal } from '../../../providers/ModalContext';

const request = {
  captcha: 0,
  cpf: 0,
  hired: 0,
  hiredTotal: 0,
  usedTotal: 0,
  available: 0,
  price: 0,
  profile: 0,
  reader: 0,
  serpro: 0,
  validation: 0,
  used: 0
};

const skeletonCards = ['requests', 'reader', 'censored', 'analyzed'];

const CountRequest = ({ inicialDate, finalDate }) => {
  const [data, setData] = useState(request);
  const [balance, setBalance] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [type, setType] = useState('');
  const { openModal } = useModal();

  const client = localStorage.getItem('client')
    ? JSON.parse(localStorage.getItem('client'))
    : '';

  const checkToken = () => {
    const decodedToken = jwtDecode(getToken());
    if (decodedToken.sub.chargeType) {
      setType(decodedToken.sub.chargeType);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const { CancelToken } = axios;
      const source = CancelToken.source();
      setRefresh(true);
      const apiUrl = `${process.env.REACT_APP_WAS_KEY}/requestCount`;
      const res = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { inicialDate, finalDate },
        cancelToken: source.token
      });
      let totals = res.data;
      if (totals.available == null || totals.hiredTotal == null) {
        const totalResponse = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${getToken()}` },
          cancelToken: source.token
        });
        totals = totalResponse.data;
      }
      const hiredTotal = Number(totals.hiredTotal ?? totals.hired ?? 0);
      const usedTotal = Number(totals.usedTotal ?? totals.used ?? 0);
      setData({
        ...res.data,
        hiredTotal,
        usedTotal,
        available: totals.available ?? Math.max(0, hiredTotal - usedTotal)
      });
      return () => {
        source.cancel();
      };
    } finally {
      setRefresh(false);
    }
  }, [inicialDate, finalDate]);

  const getBalance = useCallback(() => {
    const url = `${process.env.REACT_APP_WAS_KEY}/wallet/balance`;

    return axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'x-api-key': client.owner
        }
      })
      .then((res) => {
        setBalance(res.data.balance);
      });
  }, [client]);

  useEffect(() => {
    getBalance();
  }, [getBalance]);

  useEffect(() => {
    fetchData().catch((e) => {
      throw e;
    });
  }, [fetchData]);

  useEffect(() => {
    checkToken();
  }, []);

  const goToRecharge = () => {
    openModal();
  };

  const refreshDashboard = () => {
    fetchData().catch((e) => {
      throw e;
    });
    getBalance().catch((e) => {
      throw e;
    });
  };

  const getPercent = (value, total) => (total ? +((value * 100) / total).toFixed() : 0);
  const availableRequests = data.available ?? 0;
  const requestCards = [
    {
      metricId: 'requests',
      title: 'Requisições disponíveis',
      value: availableRequests,
      percent: getPercent(availableRequests, data.hiredTotal ?? 0)
    },
    {
      metricId: 'reader',
      title: 'Notas lidas',
      value: data.reader || 0,
      percent: getPercent(data.reader || 0, data.used || 0)
    },
    {
      metricId: 'censored',
      title: 'Notas censuradas',
      value: data.serpro || 0,
      percent: getPercent(data.serpro || 0, data.used || 0)
    },
    {
      metricId: 'analyzed',
      title: 'Notas analisadas',
      value: data.validation || 0,
      percent: getPercent(data.validation || 0, data.used || 0)
    }
  ];

  return (
    <>
      {type === 'CREDIT' && (
        <Col xs={12} className="mb-3 mb-md-4">
          <Card className="count-request__balance-card w-100 d-flex flex-column">
            <CardBody className="dashboard__card-widget count-request__balance-card-body">
              <div className="count-request__balance">
                <div className="count-request__balance-label-row">
                  <span className="count-request__balance-eyebrow">Saldo da conta</span>
                  <button
                    type="button"
                    className="count-request__refresh-btn count-request__refresh-btn--inline"
                    aria-label="Atualizar saldo e estatísticas"
                    aria-busy={refresh}
                    disabled={refresh}
                    onClick={refreshDashboard}
                  >
                    <AutorenewIcon />
                  </button>
                </div>
                <div className="count-request__balance-main">
                  <div className="count-request__balance-figure">
                    <span className="count-request__balance-icon-wrap" aria-hidden="true">
                      <WalletIcon className="count-request__wallet-icon" />
                    </span>
                    <p className="count-request__balance-amount">
                      <span className="count-request__balance-currency">R$</span>
                      {brMoneyMask(balance)}
                    </p>
                  </div>
                  <p className="count-request__credit-helper">
                    Crédito para consultas e serviços contratados.
                  </p>
                  <button
                    type="button"
                    className="count-request__recharge-btn"
                    onClick={goToRecharge}
                  >
                    <span className="count-request__recharge-btn-inner">
                      <WalletIcon className="count-request__recharge-icon" />
                      Recarregar
                    </span>
                  </button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      )}
      <Col xs={12}>
        <div
          className="count-request__metrics-wrap position-relative w-100"
          aria-busy={refresh}
          aria-live="polite"
        >
          <Row className="align-items-start count-request__metrics-row">
            {refresh
              ? skeletonCards.map((metricId) => (
                  <Col key={metricId} xs={12} md={6} xl={3}>
                    <Card
                      className="count-request__metric-card card--not-full-height count-request__metric-card--loading"
                      aria-hidden="true"
                    >
                      <CardBody className="count-request__metric-card-body">
                        <div className="count-request__metric-top">
                          <div className="count-request__metric-skeleton-icon" />
                          <div className="count-request__metric-skeleton-text">
                            <div className="count-request__metric-skeleton-value" />
                            <div className="count-request__metric-skeleton-label" />
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))
              : requestCards.map(({ metricId, title, value, percent }) => (
                  <Col key={metricId} xs={12} md={6} xl={3}>
                    <CountRequestType
                      metricId={metricId}
                      title={title}
                      value={value}
                      percent={percent}
                    />
                  </Col>
                ))}
          </Row>
        </div>
      </Col>
    </>
  );
};

CountRequest.propTypes = {
  finalDate: PropTypes.string.isRequired,
  inicialDate: PropTypes.string.isRequired
};

export default CountRequest;
